<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Hooks
	import { beforeUpdate } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { action_title_config } from '../../../helpers/actions';
	import { get_local_ip } from '../../../helpers/interface_helpers';
	//Stores
	import { pending_commands } from 'src/stores/device-runtime/process';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	//Services
	import { postReq } from '../../../services/http';
	//Utilities
	import { clearActionsUrls, handleSingleSensorSaveActions, saveAction } from './../helpers';
	//Design system components
	import { Radio, Text, toast, Container, Stack } from '@Shelly/shelly-design-system';

	export let editableObject;

	let deviceActions = Object.assign({}, { ...editableObject });
	const device = deviceActions?.device;

	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	let _actions = deviceActions?.[deviceActions.selectedDevices[0]] || deviceActions?.[device.id];
	let isThermostat = $devices?.[device?.id]?.category === 'thermostat';
	$: windowReport = deviceActions?.condition === deviceActions?.options?.[0] && isThermostat;

	let conditions;
	let selected = deviceActions?.selectedAction;
	let condition;
	let enabled = false;

	/**
	 * @function setConditions we generate action list to be displayed to the final modal page.
	 * @param actions are all actions.
	 * @param filterMore is used only for Door window case, because we will need to display choices based on light condition
	 */
	const setConditions = (actions, filterMore = false) => {
		if (!filterMore) {
			conditions = Object.entries(actions).filter(
				(name, _) =>
					name[0] !== 'category' &&
					name[0] !== 'ips' &&
					!name[0].includes('alarm') &&
					!name[0].includes('off'),
			);
		} else {
			conditions = Object.entries(actions).filter(
				(name) =>
					name[0].includes('open') || name[0].includes('twilight') || name[0].includes('dark'),
			);
		}
	};

	setConditions(_actions); //setting conditions not on mount, because data isn't ready yet.

	/**
	 * @function setBlindTime will be triggered only if user was changed the blind time's value.
	 * @param value is the value for motion's inactive time
	 */
	const setBlindTime = async (value) => {
		const res = await postReq(
			`/device/generic_command`,
			{
				id: device.id,
				command: `motion_blind_time_minutes`,
				payload: JSON.stringify({ value: +value }),
			},
			10000,
		);

		if (res.isok) {
			const deviceSettings = readDeviceSettings(device.id) ?? settings;
			deviceSettings.motion.blind_time_minutes = value;
			devices_settings.update_devices_settings(device.id, deviceSettings);
			return toast.open({
				type: 'success',
				title: '',
				text: $format('motion_blind_time_scc_toast', {
					values: { value },
					default: 'Blind time set to {value}',
				}),
			});
		} else {
			return toast.open({
				type: 'danger',
				title: '',
				text: $format('motion_blind_time_err_toast') || 'Failed to set Blind time.',
			});
		}
	};

	/**
	 * @function handleSave removing all urls from current actions. Then if blind time was changed requesting bling time change.
	 * After that based on collected ip addresses generating url addresses. Sending mostly on/off action requests.
	 */
	const handleSave = async () => {
		const ips = deviceActions.ips || [];
		// deviceActions.loading = true;
		if (isThermostat && windowReport) {
			_actions = clearActionsUrls(deviceActions[deviceActions.selectedDevices[0]]);
			ips.push({
				...$devices[device.id],
				ip: get_local_ip(device.id),
			});
		} else {
			_actions = clearActionsUrls(deviceActions[device.id]);
		}
		_actions[condition].enabled = true;
		if (typeof deviceActions.blindTime !== 'undefined') {
			await setBlindTime(deviceActions.blindTime);
		}
		let resOn, resOff, _id;
		ips?.forEach(async (element) => {
			let category = element.category !== _actions.category ? element.category : _actions.category;

			_actions[condition].urls.push({
				url: `http://${element.ip}/${category}/${$devices[element.id].channel}?turn=${
					condition.includes('off') && !condition.includes('alarm') ? 'off' : 'on'
				}`,
				int: '0000-0000',
			});
			_actions[condition].enabled = true;
			Object.entries(_actions).forEach(async (action) => {
				if (action[0].includes('off') && !action[0].includes('alarm')) {
					_actions[action[0]].urls.push({
						url: `http://${element.ip}/${category}/${$devices[element.id].channel}?turn=off`,
						int: '0000-0000',
					});
					_actions[action[0]].enabled = true;
				}
			});
		});

		resOn = saveAction(device.id, condition, _actions, $devices);
		Object.entries(_actions).forEach(async (action) => {
			if (action[0].includes('off') && !action[0].includes('alarm')) {
				resOff = saveAction(device.id, action[0], _actions, $devices);
			}
		});

		Promise.all([resOn, resOff]).then((res) => {
			if (res[0]?.isok && res[1]?.isok) {
				const pending_status = $pending_commands[device.id];
				if (pending_status) {
					toast.open({
						type: 'info',
						title: $format('info_apply_setting_on_wake_up'),
					});
				} else {
					toast.open({
						type: 'success',
						title: $format('setting_scc_saved_toast'),
					});
				}
			}
			deviceActions.loading = false;
			deviceActions.blindTime = undefined;
			deviceActions.callback = undefined;
			deviceActions.selectedDevices = [];
			deviceActions.category = undefined;
			deviceActions.ips = [];
			deviceActions.selectedAction = undefined;
			editableObject = deviceActions;
		});
	};

	/**
	 * @function handleActionSelection basically updates the editableObject with
	 * @param action is selected actions name
	 * @param value is selected actions value
	 */
	const handleActionSelection = async (action, value) => {
		condition = action[0];
		enabled = true;
		if (typeof selected !== 'object' && value === selected) {
			selected = undefined;
		} else {
			selected = value;
		}
		if (typeof selected !== 'undefined' && deviceActions.disabled) {
			//disabling save button
			deviceActions.disabled = false;
		} else if (typeof selected === 'undefined' && !deviceActions.disabled) {
			//enabling save button
			deviceActions.disabled = true;
		}
		deviceActions.selectedAction = selected;

		if (!windowReport) {
			deviceActions.callback = handleSave; //adding callback function to save button with updated values
		} else {
			deviceActions.callback = async () => {
				await handleSingleSensorSaveActions(
					device,
					$devices,
					deviceActions.selectedDevices,
					deviceActions,
					windowReport,
					condition,
				);
			};
		}
		editableObject = deviceActions;
	};

	/**
	 * @method beforeUpdate manipulating the wizzard's tabs and buttons.
	 * This is needed if we navigate with back button to be able again to use next button or tab.
	 * Plus if window report is selected for trv and door
	 * window devиce is chosen we setting actions to dw's actions
	 */
	beforeUpdate(() => {
		if (
			windowReport &&
			deviceActions.selectedDevices.length === 1 &&
			typeof deviceActions[deviceActions.selectedDevices[0]] !== 'undefined'
		) {
			_actions = deviceActions[deviceActions.selectedDevices[0]];
			setConditions(_actions, windowReport);
		}

		if (typeof selected === 'undefined' && !deviceActions.disabled) {
			deviceActions.disabled = true; //prevent sending all actions if no selection was made
		}

		editableObject = deviceActions;
	});
</script>

<Container padding="0">
	<Text class="mb-m" styleType="h2">
		{$format('actions_conditions_description') || 'Select which condition will trigger action:'}
	</Text>

	<Stack gap="xs">
		{#each conditions as condition, value}
			<Radio
				size="m"
				{selected}
				{value}
				on:click={() => handleActionSelection(condition, value)}
				id="radio-sll31u1"
			>
				{action_title_config(condition[0]).replace('(URLs)', '')}
			</Radio>
		{/each}
	</Stack>
</Container>
